import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { FileDown, Send, Copy, CheckCircle2, Clock3, XCircle } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";

// Phase 17-D item 8: Recognition -> Evidence tab.
// - Pack generator: date range + org filter over APPROVED hours.
// - My packs list (view/print + verification code).
// - Assessor reports: request per org -> org approves -> copy-ready eDofE text.
export function EvidencePanel() {
  const [orgs, setOrgs] = useState([]);
  const [packs, setPacks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, "year").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [orgFilter, setOrgFilter] = useState([]); // org_user_ids; empty = all
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.evidenceEligibleOrgs().then(setOrgs).catch(() => {});
    api.myEvidencePacks().then(setPacks).catch(() => {});
    api.assessorRequests().then(setRequests).catch(() => {});
  };
  useEffect(load, []);

  const toggleOrg = (id) =>
    setOrgFilter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const pack = await api.createEvidencePack({ from_date: fromDate, to_date: toDate, org_user_ids: orgFilter });
      toast.success(`Pack created — ${pack.total_hours}h across ${pack.entry_count} sessions`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create the pack");
    } finally { setBusy(false); }
  };

  const requestReport = async (org) => {
    try {
      const r = await api.createAssessorRequest(org.org_user_id, "");
      toast.success(r.created === false
        ? `Already requested — ${org.org_name} has it in their queue`
        : `Request sent to ${org.org_name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the request");
    }
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied — paste it into eDofE"); }
    catch { toast("Copy failed — select the text manually"); }
  };

  const reqForOrg = (orgId) => requests.find((r) => r.org_user_id === orgId && r.status === "pending");

  if (orgs.length === 0) {
    return (
      <div data-testid="evidence-empty" className="bg-white border border-dashed border-neutral-300 rounded-2xl p-10 text-center">
        <FileDown className="w-9 h-9 text-neutral-300 mx-auto" />
        <div className="font-display text-xl text-forest mt-3">Evidence packs unlock with approved hours</div>
        <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
          Once an organisation approves your logged hours, you can generate a date-ranged,
          verifiable pack for DofE, UCAS or CVs.
        </p>
        <Link to="/opportunities" className="inline-block mt-5 text-forest font-medium underline">Find opportunities</Link>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="evidence-panel">
      {/* Generator */}
      <form onSubmit={generate} className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="evidence-generator">
        <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Generate an evidence pack</div>
        <p className="text-sm text-neutral-500 mt-1.5">
          Pick a date range (and organisations, if you want a subset) — you'll get a printable pack with a public verification code.
        </p>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <label className="text-xs text-neutral-600">
            From
            <Input data-testid="evidence-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-10 mt-1 rounded-xl" />
          </label>
          <label className="text-xs text-neutral-600">
            To
            <Input data-testid="evidence-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-10 mt-1 rounded-xl" />
          </label>
          <Button type="submit" disabled={busy} data-testid="evidence-generate-btn" className="rounded-full bg-forest hover:bg-forest-hover h-10 px-6">
            {busy ? "Generating…" : "Generate pack"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {orgs.map((o) => (
            <button
              key={o.org_user_id}
              type="button"
              data-testid={`evidence-org-toggle-${o.org_user_id}`}
              onClick={() => toggleOrg(o.org_user_id)}
              className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${
                orgFilter.includes(o.org_user_id)
                  ? "bg-forest text-white border-forest"
                  : "bg-white text-neutral-600 border-neutral-200 hover:border-forest/40"
              }`}
            >
              {o.org_name} · {o.hours}h
            </button>
          ))}
          <span className="text-[11px] text-neutral-400 self-center">none selected = all organisations</span>
        </div>
      </form>

      {/* My packs */}
      <div data-testid="evidence-packs-list">
        <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">My packs</div>
        {packs.length === 0 ? (
          <p className="text-sm text-neutral-500 mt-2">No packs yet — generate your first one above.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {packs.map((p) => (
              <div key={p.id} className="bg-white border border-neutral-200 rounded-xl px-5 py-4 flex items-center justify-between flex-wrap gap-3" data-testid={`evidence-pack-row-${p.id}`}>
                <div>
                  <div className="text-sm font-medium text-forest">
                    {dayjs(p.from_date).format("D MMM YYYY")} — {dayjs(p.to_date).format("D MMM YYYY")} · {p.total_hours}h · {p.entry_count} sessions
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5 font-mono">{p.code}</div>
                </div>
                <Link to={`/evidence/${p.id}`} data-testid={`evidence-pack-open-${p.id}`}>
                  <Button variant="outline" className="rounded-full h-9 px-4 text-xs">View & print</Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assessor reports */}
      <div data-testid="assessor-section">
        <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Assessor reports (DofE)</div>
        <p className="text-sm text-neutral-500 mt-1.5">
          Ask an organisation to approve a short assessor report — once approved, copy it straight into eDofE.
          A declined or unanswered request never blocks your evidence packs.
        </p>
        <div className="mt-3 space-y-2">
          {orgs.map((o) => {
            const pending = reqForOrg(o.org_user_id);
            const answered = requests.filter((r) => r.org_user_id === o.org_user_id && r.status !== "pending");
            return (
              <div key={o.org_user_id} className="bg-white border border-neutral-200 rounded-xl px-5 py-4" data-testid={`assessor-org-${o.org_user_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="text-sm font-medium text-forest">{o.org_name}</div>
                  {pending ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid={`assessor-pending-${o.org_user_id}`}>
                      <Clock3 className="w-3.5 h-3.5" /> Awaiting {o.org_name}
                    </span>
                  ) : (
                    <Button
                      data-testid={`assessor-request-btn-${o.org_user_id}`}
                      onClick={() => requestReport(o)}
                      variant="outline"
                      className="rounded-full h-9 px-4 text-xs"
                    >
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Request report
                    </Button>
                  )}
                </div>
                {answered.map((r) => (
                  <div key={r.id} className="mt-3 border-t border-neutral-100 pt-3" data-testid={`assessor-result-${r.id}`}>
                    {r.status === "approved" ? (
                      <>
                        <div className="text-xs text-green-700 inline-flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Approved {r.responded_at ? dayjs(r.responded_at).format("D MMM YYYY") : ""}
                        </div>
                        <p className="text-sm text-neutral-700 mt-2 whitespace-pre-line bg-cream/60 border border-neutral-200 rounded-xl p-4">{r.final_text}</p>
                        <Button
                          data-testid={`assessor-copy-${r.id}`}
                          onClick={() => copyText(r.final_text)}
                          variant="outline"
                          className="rounded-full h-8 px-3.5 text-xs mt-2"
                        >
                          <Copy className="w-3 h-3 mr-1.5" /> Copy for eDofE
                        </Button>
                      </>
                    ) : (
                      <div className="text-xs text-neutral-500 inline-flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5 text-neutral-400" /> Declined{r.decline_note ? ` — ${r.decline_note}` : ""} · your packs are unaffected
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
